'use client';

import { useEffect, useState } from 'react';
import { retrieveRawInitData } from '@telegram-apps/sdk-react';

/**
 * Telegram raw initData with a dev-mode override.
 *
 * Phase 0.7 attempted to fix the hook-order/SSR mismatch by always
 * calling `@telegram-apps/sdk-react`'s `useRawInitData` — which works
 * for hook stability but throws `LaunchParamsRetrieveError` when the
 * SDK can't find Telegram launch params (i.e. anywhere outside TMA).
 * That broke every web visitor reaching an (auth) route.
 *
 * The real fix: don't call the SDK hook at all. Read
 * `window.Telegram.WebApp.initData` (TMA injects it on launch) and
 * the URL hash (`tgWebAppData=...`, set on initial open) inside a
 * `useEffect`, store the result in `useState`. Hook count is now
 * `useState + useEffect` — stable across SSR, hydration, dev/prod,
 * TMA/web. Returns `undefined` until the effect runs (matches
 * legacy SSR-friendly behaviour); consumers who need a non-empty
 * value already gate on `apiClient`/`AuthBootstrap` ready states.
 */
interface InitDataSnapshot {
  data: string | undefined;
  /** True after the client-side detection effect has run at least
   * once. Lets callers distinguish "still loading, might become a
   * real value" (`!ready && data === undefined`) from "we checked,
   * not in TMA" (`ready && data === undefined`). */
  ready: boolean;
}

const INITIAL_SNAPSHOT: InitDataSnapshot = { data: undefined, ready: false };

function useRawInitDataSnapshot(): InitDataSnapshot {
  const [snapshot, setSnapshot] = useState<InitDataSnapshot>(INITIAL_SNAPSHOT);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const devInitData = process.env.NEXT_PUBLIC_DEV_INIT_DATA;
      if (devInitData) {
        setSnapshot({ data: devInitData, ready: true });
        return;
      }
    }

    if (typeof window === 'undefined') {
      setSnapshot({ data: undefined, ready: true });
      return;
    }

    let resolved: string | undefined;

    // Primary source: SDK's launch-param retriever. Parses the URL
    // hash on first call and caches into sessionStorage, so it returns
    // the raw initData string even after the TMA shell's init() has
    // stripped the hash from the URL. Throws LaunchParamsRetrieveError
    // outside TMA — fall through to fallbacks.
    try {
      const sdkInitData = retrieveRawInitData();
      if (sdkInitData) resolved = sdkInitData;
    } catch {
      // Not in TMA — fall through.
    }

    // Fallback 1: telegram-web-app.js global.
    if (!resolved) {
      const tgInitData = (window as unknown as {
        Telegram?: { WebApp?: { initData?: string } };
      }).Telegram?.WebApp?.initData;
      if (tgInitData) resolved = tgInitData;
    }

    // Fallback 2: parse the URL hash directly.
    if (!resolved) {
      const hash = window.location.hash || '';
      if (hash.includes('tgWebAppData=')) {
        const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
        const initData = params.get('tgWebAppData');
        if (initData) resolved = decodeURIComponent(initData);
      }
    }

    setSnapshot({ data: resolved, ready: true });
  }, []);

  return snapshot;
}

export function useRawInitDataWithDev(): string | undefined {
  return useRawInitDataSnapshot().data;
}

/** True after the client-side TMA-detection effect has run. Use to
 * gate UI that needs a definitive anon-vs-authed answer (e.g. the
 * RequiresLogin wall) so it doesn't flash on TMA visitors during the
 * one render before the effect resolves. */
export function useRawInitDataReady(): boolean {
  return useRawInitDataSnapshot().ready;
}

/**
 * Парсит initData для получения объекта user
 */
export function parseInitData(initData: string | undefined) {
  if (!initData) return null;

  try {
    const params = new URLSearchParams(initData);
    const userParam = params.get('user');

    if (userParam) {
      return JSON.parse(decodeURIComponent(userParam));
    }
  } catch (error) {
    console.error('Failed to parse initData:', error);
  }

  return null;
}

/**
 * Хук для получения данных пользователя из initData
 */
export function useInitDataUser() {
  const initData = useRawInitDataWithDev();
  return parseInitData(initData);
}

/**
 * Хук для проверки премиум статуса пользователя.
 * В web режиме возвращает false; в dev режиме без initData
 * возвращает true для удобства локальной разработки.
 */
export function useIsPremium(): boolean {
  const user = useInitDataUser();
  const isPremium = user?.is_premium === true;

  const isDev = process.env.NODE_ENV !== 'production';
  if (isDev && !user?.is_premium) {
    return true;
  }

  return isPremium;
}
